import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { DocumentItem } from '@/features/marketing/components/DocumentListTab';
import { useToast } from '@/contexts/ToastContext';

interface UseDocumentListProps {
  enabled: boolean;
  userId?: string;
}

/**
 * Hook to manage document list tab state and operations
 */
export const useDocumentList = ({ enabled, userId }: UseDocumentListProps) => {
  const { addToast } = useToast();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [docPageIndex, setDocPageIndex] = useState(0);
  const [docPageSize, setDocPageSize] = useState(10);
  const [docTotal, setDocTotal] = useState(0);
  const [docSearch, setDocSearch] = useState('');

  const fetchDocuments = useCallback(
    async (pageIndex = 0, pageSize = 10, search = '') => {
      if (!enabled) return;

      try {
        setDocumentsLoading(true);
        setDocumentsError(null);

        const offset = pageIndex * pageSize;
        const endpoint = userId ? '/mxm/internal/docs' : '/mxm/posts/all-upload';

        const res = await api.get<{ data: DocumentItem[]; total: number }>(
          endpoint,
          {
            params: {
              limit: pageSize,
              offset,
              ...(userId && { userId }),
              ...(search && { search }),
            },
          },
        );

        setDocuments(res.data.data || []);
        setDocTotal(res.data.total ?? 0);
      } catch (err: unknown) {
        console.error('Failed to fetch documents', err);
        const msg = getErrorMessage(err);
        setDocumentsError(Array.isArray(msg) ? msg.join(', ') : msg);
      } finally {
        setDocumentsLoading(false);
      }
    },
    [enabled, userId],
  );

  // Fetch documents when pagination or search changes
  useEffect(() => {
    if (enabled) {
      fetchDocuments(docPageIndex, docPageSize, docSearch);
    }
  }, [enabled, docPageIndex, docPageSize, docSearch, fetchDocuments]);

  const filteredDocuments = useMemo(
    () =>
      docSearch.trim()
        ? documents.filter((doc) =>
          doc.title?.toLowerCase().includes(docSearch.toLowerCase()),
        )
        : documents,
    [documents, docSearch],
  );

  const handleDocumentSearch = useCallback(async (query: string) => {
    setDocSearch(query);
    // FE-only filter, no API call
  }, []);

  const handleDocumentPostToLinkedIn = useCallback(async (
    documentId: string,
    openLinkedInModal: (closable: boolean) => void,
    hasLinkedInToken: boolean
  ) => {
    if (!hasLinkedInToken) {
      openLinkedInModal(true);
      return;
    }

    try {
      addToast('Posting document to LinkedIn...', 'info');

      const response = await api.post(
        '/mxm/posts/documents/post-to-linkedin',
        {}, // no body, description handled on backend
        {
          params: {
            id: documentId, // becomes ?id=<uuid>
          },
        },
      );

      addToast(
        `Successfully posted document to LinkedIn! Post URL: ${response.data.url}`,
        'success',
      );
    } catch (error: unknown) {
      console.error('Failed to post document to LinkedIn:', error);
      addToast(getErrorMessage(error), 'error');
    }
  }, [addToast]);

  return {
    documents: filteredDocuments,
    documentsLoading,
    documentsError,
    pagination: {
      pageIndex: docPageIndex,
      pageSize: docPageSize,
      totalCount: docTotal,
    },
    docSearch,
    handleDocumentSearch,
    handlePaginationChange: ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
      setDocPageIndex(pageIndex);
      setDocPageSize(pageSize);
    },
    handleDocumentPostToLinkedIn,
    onDocumentUploaded: (_docUrl: string) => {
      // Refetch documents after upload
      fetchDocuments(docPageIndex, docPageSize);
    },
    deleteDocument: async (id: string) => {
      try {
        await api.delete(`/mxm/posts/document/${id}`);
        addToast('Document deleted successfully', 'success');
        // Refetch documents after delete
        fetchDocuments(docPageIndex, docPageSize);
      } catch (error: unknown) {
        console.error('Failed to delete document:', error);
        addToast(getErrorMessage(error), 'error');
      }
    },
  };
};

